import { useRef, useState } from "react";
import { ArrowDown, ArrowUp, FolderOpen, Loader2, Plus, Trash2, Upload, X } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import ImageField from "./ImageField";
import MediaPicker from "./MediaPicker";

const labelCls = "mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-[#5C5C5C]";
const inputCls =
  "w-full rounded-xl border border-line bg-cream/50 px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-rose";

// Field types: text | textarea | lines | pairs | quads | image | video | imagelist | toggle
const SCHEMAS = {
  hero: [
    { k: "kicker", t: "text", l: "Kicker (small top line)" },
    { k: "heading", t: "lines", l: "Heading — one line per row, *word* = italic rose" },
    { k: "subtext", t: "textarea", l: "Subtext" },
    { k: "primary_label", t: "text", l: "Primary button label" },
    { k: "secondary_label", t: "text", l: "Secondary button label (WhatsApp)" },
    { k: "image", t: "image", l: "Hero image" },
    { k: "video", t: "video", l: "Hero video (optional — replaces image)" },
    { k: "badge_title", t: "text", l: "Floating badge title" },
    { k: "badge_sub", t: "text", l: "Floating badge subtitle" },
  ],
  marquee: [{ k: "items", t: "lines", l: "Phrases (one per line)" }],
  categories: [
    { k: "kicker", t: "text", l: "Kicker" }, { k: "title", t: "text", l: "Heading (*word* = italic)" }, { k: "blurb", t: "textarea", l: "Side text" },
  ],
  products: [
    { k: "kicker", t: "text", l: "Kicker" }, { k: "title", t: "text", l: "Heading" }, { k: "blurb", t: "textarea", l: "Side text" },
  ],
  manifesto: [
    { k: "kicker", t: "text", l: "Kicker" }, { k: "title", t: "text", l: "Heading" },
    { k: "text", t: "textarea", l: "Intro text" }, { k: "image", t: "image", l: "Image" },
    { k: "benefits", t: "pairs", l: "Benefits — one per line: Title | Description" },
  ],
  occasions: [
    { k: "kicker", t: "text", l: "Kicker" }, { k: "title", t: "text", l: "Heading" }, { k: "blurb", t: "textarea", l: "Side text" },
    { k: "items", t: "lines", l: "Occasions (one per line)" },
    { k: "cta_kicker", t: "text", l: "CTA kicker" }, { k: "cta_heading", t: "text", l: "CTA heading" },
    { k: "cta_text", t: "textarea", l: "CTA text" }, { k: "cta_button", t: "text", l: "CTA button label" },
  ],
  testimonials: [
    { k: "kicker", t: "text", l: "Kicker" }, { k: "title", t: "text", l: "Heading" }, { k: "blurb", t: "textarea", l: "Side text" },
    { k: "items", t: "testimonials", l: "Testimonials" },
  ],
  instagram: [
    { k: "kicker", t: "text", l: "Kicker" }, { k: "title", t: "text", l: "Heading" }, { k: "blurb", t: "textarea", l: "Text" },
    { k: "images", t: "imagelist", l: "Gallery images (one path/URL per line — paste from Media)" },
  ],
  whatsapp_cta: [
    { k: "kicker", t: "text", l: "Kicker" }, { k: "title", t: "text", l: "Heading" },
    { k: "heading", t: "text", l: "Card heading" }, { k: "text", t: "textarea", l: "Text" },
    { k: "button_label", t: "text", l: "Button label" }, { k: "payment_note", t: "text", l: "Small note (e.g. No COD)" },
  ],
  text: [{ k: "heading", t: "text", l: "Heading" }, { k: "body", t: "textarea", l: "Text (blank line = new paragraph)" }],
  image_text: [
    { k: "heading", t: "text", l: "Heading" }, { k: "body", t: "textarea", l: "Text" },
    { k: "image", t: "image", l: "Image" }, { k: "flip", t: "toggle", l: "Image on the right" },
    { k: "button_label", t: "text", l: "Button label (optional)" }, { k: "button_url", t: "text", l: "Button link (#contact, /p/... or https://...)" },
  ],
  video: [
    { k: "heading", t: "text", l: "Heading" }, { k: "description", t: "textarea", l: "Description" },
    { k: "video", t: "video", l: "Video (MP4/WebM upload or URL)" }, { k: "poster", t: "image", l: "Poster / thumbnail image" },
    { k: "autoplay", t: "toggle", l: "Autoplay" }, { k: "muted", t: "toggle", l: "Muted" },
    { k: "loop", t: "toggle", l: "Loop" }, { k: "controls", t: "toggle", l: "Show controls" },
  ],
  banner: [
    { k: "heading", t: "text", l: "Banner text" }, { k: "button_label", t: "text", l: "Button label" }, { k: "button_url", t: "text", l: "Button link" },
  ],
  cta: [
    { k: "heading", t: "text", l: "Heading" }, { k: "text", t: "textarea", l: "Text" },
    { k: "button_label", t: "text", l: "Button label" }, { k: "button_url", t: "text", l: "Button link" },
  ],
  gallery: [
    { k: "heading", t: "text", l: "Heading" }, { k: "images", t: "imagelist", l: "Images (one path/URL per line)" },
  ],
};

export default function SectionEditor({ section, onClose, onSaved }) {
  const [name, setName] = useState(section.name);
  const [content, setContent] = useState({ ...(section.content || {}) });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  // photoTarget: { type: "imagelist", k } | { type: "testimonial", k, i } — where an uploaded/picked path goes
  const [photoTarget, setPhotoTarget] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const fileRef = useRef(null);
  const fields = SCHEMAS[section.type] || [];

  const set = (k, v) => setContent((c) => ({ ...c, [k]: v }));

  const applyPhoto = (path) => {
    if (!photoTarget) return;
    if (photoTarget.type === "imagelist") {
      const cur = content[photoTarget.k];
      const list = Array.isArray(cur) ? cur : [];
      set(photoTarget.k, [...list, path]);
    } else if (photoTarget.type === "testimonial") {
      const items = Array.isArray(content[photoTarget.k]) ? content[photoTarget.k] : [];
      set(photoTarget.k, items.map((t, x) => (x === photoTarget.i ? { ...t, image: path } : t)));
    }
    setPhotoTarget(null);
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("image", file);
      const { data } = await api.post("/admin/upload", fd);
      applyPhoto(data.path);
    } catch {
      setError("Image upload failed (JPG/PNG/WebP, max 5 MB).");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await api.patch(`/admin/sections/${section.id}`, { name, content });
      onSaved();
    } catch {
      setError("Could not save the section.");
      setSaving(false);
    }
  };

  const renderField = (f) => {
    const v = content[f.k];
    if (f.t === "textarea")
      return <textarea data-testid={`sf-${f.k}`} rows={3} value={v || ""} onChange={(e) => set(f.k, e.target.value)} className={`${inputCls} resize-none`} />;
    if (f.t === "lines")
      return <textarea data-testid={`sf-${f.k}`} rows={4} value={Array.isArray(v) ? v.join("\n") : v || ""} onChange={(e) => set(f.k, e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} className={`${inputCls} resize-none`} />;
    if (f.t === "imagelist")
      return (
        <div>
          <textarea data-testid={`sf-${f.k}`} rows={4} value={Array.isArray(v) ? v.join("\n") : v || ""} onChange={(e) => set(f.k, e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} className={`${inputCls} resize-none`} />
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              data-testid={`sf-${f.k}-upload`}
              onClick={() => { setPhotoTarget({ type: "imagelist", k: f.k }); fileRef.current?.click(); }}
              disabled={uploading}
              className="inline-flex items-center gap-1.5 rounded-full border border-line px-3.5 py-2 text-xs font-medium transition-colors hover:border-rose hover:text-rose disabled:opacity-60"
            >
              {uploading && photoTarget?.k === f.k ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload Image
            </button>
            <button
              type="button"
              data-testid={`sf-${f.k}-media`}
              onClick={() => { setPhotoTarget({ type: "imagelist", k: f.k }); setPickerOpen(true); }}
              className="inline-flex items-center gap-1.5 rounded-full border border-line px-3.5 py-2 text-xs font-medium transition-colors hover:border-rose hover:text-rose"
            >
              <FolderOpen size={13} /> Choose from Media
            </button>
          </div>
        </div>
      );
    if (f.t === "pairs")
      return (
        <textarea data-testid={`sf-${f.k}`} rows={5} className={`${inputCls} resize-none`}
          value={(v || []).map((b) => `${b.title} | ${b.description}`).join("\n")}
          onChange={(e) => set(f.k, e.target.value.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => { const [title, ...rest] = l.split("|"); return { title: title.trim(), description: rest.join("|").trim() }; }))} />
      );
    if (f.t === "testimonials") {
      const items = Array.isArray(v) ? v : [];
      const update = (i, patch) => set(f.k, items.map((t, x) => (x === i ? { ...t, ...patch } : t)));
      const move = (i, dir) => {
        const j = i + dir;
        if (j < 0 || j >= items.length) return;
        const next = [...items];
        [next[i], next[j]] = [next[j], next[i]];
        set(f.k, next);
      };
      return (
        <div data-testid={`sf-${f.k}`} className="flex flex-col gap-4">
          {items.map((t, i) => (
            <div key={i} data-testid={`testimonial-item-${i}`} className="flex flex-col gap-3 rounded-2xl border border-line p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full border border-line bg-cream text-[9px] text-[#5C5C5C]">
                  {t.image ? <img src={imgUrl(t.image)} alt="" className="h-full w-full object-cover" /> : "No photo"}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    data-testid={`testimonial-photo-upload-${i}`}
                    onClick={() => { setPhotoTarget({ type: "testimonial", k: f.k, i }); fileRef.current?.click(); }}
                    disabled={uploading}
                    className="inline-flex items-center gap-1.5 rounded-full border border-line px-3.5 py-2 text-xs font-medium transition-colors hover:border-rose hover:text-rose disabled:opacity-60"
                  >
                    {uploading && photoTarget?.type === "testimonial" && photoTarget?.i === i ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                    {t.image ? "Change Photo" : "Upload Photo"}
                  </button>
                  <button
                    type="button"
                    data-testid={`testimonial-photo-media-${i}`}
                    onClick={() => { setPhotoTarget({ type: "testimonial", k: f.k, i }); setPickerOpen(true); }}
                    className="inline-flex items-center gap-1.5 rounded-full border border-line px-3.5 py-2 text-xs font-medium transition-colors hover:border-rose hover:text-rose"
                  >
                    <FolderOpen size={13} /> Choose from Media
                  </button>
                  {t.image && (
                    <button
                      type="button"
                      data-testid={`testimonial-photo-remove-${i}`}
                      onClick={() => update(i, { image: "" })}
                      className="inline-flex items-center gap-1.5 rounded-full border border-line px-3.5 py-2 text-xs font-medium transition-colors hover:border-red-400 hover:text-red-500"
                    >
                      <X size={13} /> Remove
                    </button>
                  )}
                </div>
                <div className="ml-auto flex shrink-0 gap-1">
                  <button type="button" data-testid={`testimonial-up-${i}`} onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move testimonial up" className="rounded-full border border-line p-1.5 hover:border-rose hover:text-rose disabled:opacity-30"><ArrowUp size={12} /></button>
                  <button type="button" data-testid={`testimonial-down-${i}`} onClick={() => move(i, 1)} disabled={i === items.length - 1} aria-label="Move testimonial down" className="rounded-full border border-line p-1.5 hover:border-rose hover:text-rose disabled:opacity-30"><ArrowDown size={12} /></button>
                  <button type="button" data-testid={`testimonial-delete-${i}`} onClick={() => set(f.k, items.filter((_, x) => x !== i))} aria-label="Delete testimonial" className="rounded-full border border-line p-1.5 hover:border-red-400 hover:text-red-500"><Trash2 size={12} /></button>
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <input data-testid={`testimonial-name-${i}`} type="text" value={t.name || ""} onChange={(e) => update(i, { name: e.target.value })} placeholder="Customer name" className={inputCls} />
                <input data-testid={`testimonial-city-${i}`} type="text" value={t.city || ""} onChange={(e) => update(i, { city: e.target.value })} placeholder="City" className={inputCls} />
                <input data-testid={`testimonial-context-${i}`} type="text" value={t.context || ""} onChange={(e) => update(i, { context: e.target.value })} placeholder="What they bought (e.g. Birthday Hamper)" className={inputCls} />
                <select data-testid={`testimonial-rating-${i}`} value={t.rating || 5} onChange={(e) => update(i, { rating: Number(e.target.value) })} aria-label="Rating" className={inputCls}>
                  {[5, 4, 3, 2, 1].map((r) => (
                    <option key={r} value={r}>{r} star{r > 1 ? "s" : ""}</option>
                  ))}
                </select>
              </div>
              <textarea data-testid={`testimonial-quote-${i}`} rows={2} value={t.quote || ""} onChange={(e) => update(i, { quote: e.target.value })} placeholder="Review" className={`${inputCls} resize-none`} />
            </div>
          ))}
          <button
            type="button"
            data-testid="testimonial-add"
            onClick={() => set(f.k, [...items, { name: "", city: "", context: "", quote: "", rating: 5, image: "" }])}
            className="inline-flex items-center justify-center gap-1.5 rounded-full border border-dashed border-line px-4 py-3 text-xs font-medium text-[#5C5C5C] transition-colors hover:border-rose hover:text-rose"
          >
            <Plus size={13} /> Add testimonial
          </button>
        </div>
      );
    }
    if (f.t === "image") return <ImageField label="" value={v || ""} onChange={(val) => set(f.k, val)} testid={`sf-${f.k}`} />;
    if (f.t === "video") return <ImageField label="" value={v || ""} onChange={(val) => set(f.k, val)} kind="video" testid={`sf-${f.k}`} />;
    if (f.t === "toggle")
      return (
        <label className="flex cursor-pointer items-center gap-3 text-sm font-medium">
          <input data-testid={`sf-${f.k}`} type="checkbox" checked={!!v} onChange={(e) => set(f.k, e.target.checked)} className="h-4 w-4 accent-[#C48B81]" />
          {f.l}
        </label>
      );
    return <input data-testid={`sf-${f.k}`} type="text" value={v || ""} onChange={(e) => set(f.k, e.target.value)} className={inputCls} />;
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center md:items-center md:p-8">
      <div className="absolute inset-0 bg-ink/60 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div data-testid="section-editor" role="dialog" aria-modal="true" className="relative flex max-h-[92vh] w-full max-w-2xl flex-col rounded-t-3xl bg-white md:rounded-3xl">
        <div className="flex items-center justify-between border-b border-line p-6 md:px-8">
          <input
            data-testid="section-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label="Section name"
            className="w-full max-w-sm rounded-xl border border-transparent px-2 py-1 font-serif text-2xl font-medium outline-none transition-colors focus:border-line"
          />
          <button data-testid="section-editor-close" onClick={onClose} aria-label="Close editor" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-line transition-colors hover:bg-ink hover:text-cream">
            <X size={16} />
          </button>
        </div>
        <div className="flex flex-col gap-5 overflow-y-auto p-6 md:p-8">
          {fields.map((f) => (
            <div key={f.k}>
              {f.t !== "toggle" && f.t !== "image" && f.t !== "video" && <label className={labelCls}>{f.l}</label>}
              {(f.t === "image" || f.t === "video") && <span className={labelCls}>{f.l}</span>}
              {renderField(f)}
            </div>
          ))}
          {error && <p data-testid="section-save-error" className="rounded-xl bg-red-50 px-4 py-3 text-xs font-medium text-red-600">{error}</p>}
        </div>
        <div className="flex gap-3 border-t border-line p-6 md:px-8">
          <button
            data-testid="section-save-button"
            onClick={save}
            disabled={saving}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-ink px-6 py-4 text-sm font-medium text-cream transition-colors hover:bg-rose hover:text-white disabled:opacity-60"
          >
            {saving ? <><Loader2 size={15} className="animate-spin" /> Saving…</> : "Save Section"}
          </button>
          <button onClick={onClose} className="rounded-full border border-line px-6 py-4 text-sm font-medium transition-colors hover:border-ink">
            Cancel
          </button>
        </div>
      </div>
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={uploadPhoto} className="hidden" data-testid="section-photo-file-input" />
      {pickerOpen && (
        <MediaPicker kind="image" onClose={() => { setPickerOpen(false); setPhotoTarget(null); }} onSelect={(path) => { applyPhoto(path); setPickerOpen(false); }} />
      )}
    </div>
  );
}
